import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { Field, FieldType } from "model/robot";
import { Add, Delete } from "@mui/icons-material";
import ClearIcon from '@mui/icons-material/Clear';
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface FieldEditorProps {
  sectionIndex: number;
  fieldIndex: number;
  field: Field;
  updateField: (
    sectionIndex: number,
    fieldIndex: number,
    changes: Partial<Field>
  ) => void;
  removeField: (sectionIndex: number, fieldIndex: number) => void;
}

export default function FieldEditor({
  sectionIndex,
  fieldIndex,
  field,
  updateField,
  removeField,
}: FieldEditorProps) {
  const handleValueChange = (
    valueIndex: number,
    keyOrValue: "key" | "value",
    newValue: string
  ) => {
    const newValues = [...(field.values || [])];
    newValues[valueIndex] = {
      ...newValues[valueIndex],
      [keyOrValue]: newValue,
    };
    updateField(sectionIndex, fieldIndex, { values: newValues });
  };

  const translate = useTranslations()

  const addValue = () => {
    const newValues = [...(field.values || []), { key: "", value: "" }];
    updateField(sectionIndex, fieldIndex, { values: newValues });
  };

  const removeValue = (valueIndex: number) => {
    const newValues = [...(field.values || [])];
    newValues.splice(valueIndex, 1);
    updateField(sectionIndex, fieldIndex, { values: newValues });
  };

  return (
    <Box p={2} border="1px solid #ddd" borderRadius={2} maxWidth={600}>
      <TextField
        label={getTranslation(translate, "generic.field_name")}
        value={field.name}
        onChange={(e) =>
          updateField(sectionIndex, fieldIndex, { name: e.target.value })
        }
        fullWidth
        margin="dense"
      />
      <FormControl fullWidth margin="dense">
        <InputLabel>{getTranslation(translate, "generic.type")}</InputLabel>
        <Select
          value={field.type}
          label="Tipo"
          onChange={(e) =>
            updateField(sectionIndex, fieldIndex, {
              type: e.target.value as FieldType,
            })
          }
        >
          <MenuItem value="string">{getTranslation(translate, "formtype.string")}</MenuItem>
          <MenuItem value="textarea">{getTranslation(translate, "formtype.textarea")}</MenuItem>
          <MenuItem value="number">{getTranslation(translate, "supported.userdata.type.number")}</MenuItem>
          <MenuItem value="boolean">{getTranslation(translate, "formtype.boolean")}</MenuItem>
          <MenuItem value="date">{getTranslation(translate, "pm.propertyeditor.specificproperties.repetition_details.type_of_timer.date")}</MenuItem>
          <MenuItem value="file">{getTranslation(translate, "formtype.file")}</MenuItem>
          <MenuItem value="password">{getTranslation(translate, "formtype.password")}</MenuItem>
          <MenuItem value="dropdown">{getTranslation(translate, "formtype.dropdown")}</MenuItem>
        </Select>
      </FormControl>
      {field.type === "dropdown" && (
        <Box>
          {field.values?.map((v, valueIndex) => (
            <Box key={valueIndex} display="flex" gap={1} alignItems="center">
              <TextField
                label={getTranslation(translate, "pm.mayactivities.key.label")}
                value={v.key}
                onChange={(e) =>
                  handleValueChange(valueIndex, "key", e.target.value)
                }
                margin="dense"
              />
              <TextField
                label={getTranslation(translate, "placeholder.value.label")}
                value={v.value}
                onChange={(e) =>
                  handleValueChange(valueIndex, "value", e.target.value)
                }
                margin="dense"
              />
              <IconButton onClick={() => removeValue(valueIndex)}>
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button onClick={addValue} startIcon={<Add />}>
            {getTranslation(translate, "generic.add_value")}
          </Button>
        </Box>
      )}
      {field.type !== "boolean" && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!field.required}
              onChange={(e) =>
                updateField(sectionIndex, fieldIndex, {
                  required: e.target.checked,
                })
              }
            />
          }
          label={getTranslation(translate, "register.fieldrequired")}
        />
      )}
      <Tooltip title={getTranslation(translate, "generic.remove")}>
        <IconButton
          color="error"
          onClick={() => removeField(sectionIndex, fieldIndex)}
        >
          <ClearIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}